import { useState } from "react";
import type { Anime } from "@/mocks/anime";

export default function ChapterList({ anime }: { anime: Anime }) {
  const [active, setActive] = useState(anime.chapterList[0]?.index ?? 1);
  const [playing, setPlaying] = useState(false);
  const current = anime.chapterList.find((c) => c.index === active) || anime.chapterList[0];

  return (
    <section className="w-full" aria-labelledby="chapter-title">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 id="chapter-title" className="text-lg md:text-xl font-black text-foreground-950 tracking-tight">
            章节 / 剧集列表
          </h2>
          <p className="mt-1 text-[12px] text-foreground-500">共 {anime.chapterList.length} 话已收录，点击切换查看该话简介</p>
        </div>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-semibold">
          <i className="ri-information-line" /> 演示数据
        </span>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-4">
        {anime.chapterList.map((chapter) => (
          <button
            key={chapter.index}
            type="button"
            onClick={() => {
              setActive(chapter.index);
              setPlaying(false);
            }}
            className={`shrink-0 h-10 px-3.5 rounded-md text-[12px] font-semibold transition-colors whitespace-nowrap cursor-pointer ${
              active === chapter.index
                ? "bg-primary-900 text-background-50"
                : "bg-background-100 text-foreground-700 border border-background-200 hover:border-primary-300"
            }`}
          >
            第{chapter.index}话
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-[15px] font-bold text-foreground-950 truncate">{current?.title}</h3>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-foreground-500">
              <span className="flex items-center gap-1"><i className="ri-time-line" /> {current?.duration}</span>
              <span className="flex items-center gap-1"><i className="ri-calendar-line" /> {current?.date} 上线</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setPlaying((v) => !v)}
            className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-md bg-accent-500 hover:bg-accent-600 text-primary-950 text-[12px] font-bold transition-colors whitespace-nowrap cursor-pointer shrink-0"
          >
            <i className={playing ? "ri-pause-fill" : "ri-play-fill"} />
            {playing ? "暂停演示" : "播放演示"}
          </button>
        </div>

        <div className="mt-4 rounded-md bg-background-100 border border-background-200 px-3.5 py-3">
          <p className="text-[11px] font-semibold text-foreground-400 tracking-wide mb-1">本话简介</p>
          <p className="text-[13px] leading-6 text-foreground-700">{current?.desc}</p>
        </div>

        {playing && (
          <div className="mt-3 rounded-md bg-primary-950 px-4 py-4 animate-fade-up">
            <div className="flex items-center gap-2 text-accent-400 text-[12px] font-semibold">
              <i className="ri-movie-2-line" /> 播放演示中
            </div>
            <p className="mt-1.5 text-[12px] leading-6 text-primary-200">
              这里仅为播放入口演示，不会加载真实视频流，也不会产生任何播放记录。
            </p>
            <div className="mt-3 h-1 rounded-full bg-primary-800 overflow-hidden">
              <div className="h-full w-1/3 bg-accent-500 animate-soft-pulse" />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}